/* Ayudate · Sala de videollamada
   Punto de entrada embebido a la sesión de Jitsi Meet de una solicitud
   pagada: incrusta la sala con el SDK IFrame API (external_api.js) contra
   el self-hosted meet.ayudatetutorias.com, en vez de mandar a una pestaña
   aparte como hacía antes el correo. Al ser self-hosted ya no aplica el
   modo demo de meet.jit.si (que el SDK cortaba a los 5 minutos), así que
   se puede usar el SDK completo y autenticar con el JWT que trae el
   enlace (?token=, ver generarJitsiJWT en apps-script.gs) en vez del
   <iframe> nativo sin SDK que usaba esta página antes. Hace cumplir la
   misma ventana horaria que ya validaba el servidor (verificarTokenSala en
   apps-script.gs): bloquea la entrada antes de que se habilite, avisa
   cuando empieza la tolerancia de salida y cierra sola la llamada al
   agotarse, mandando al estudiante derecho a /calificar. */

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Marca } from "../componentes/Marca";
import { ESTILOS_MINIMOS } from "../estilos/minimos";
import { api } from "../api";
import { irA_ruta } from "../lib/rutas";

const MENSAJE_ERROR_SALA = {
  MAL_FORMADO: "Este enlace no trae el código de la sesión. Revise el correo que le enviamos y abra el enlace completo, o escríbanos por WhatsApp.",
  NO_ENCONTRADA: "No encontramos ninguna sesión con este enlace. Escríbanos por WhatsApp si cree que es un error.",
  NO_DISPONIBLE: "Esta sesión no tiene una sala activa: tiene que estar pagada para poder entrar.",
};

const DOMINIO_JITSI = "meet.ayudatetutorias.com";

function formatearRestante(ms) {
  const totalSeg = Math.max(0, Math.ceil(ms / 1000));
  const min = Math.floor(totalSeg / 60);
  const seg = totalSeg % 60;
  return `${min}:${String(seg).padStart(2, "0")}`;
}

/* Carga external_api.js una sola vez por pestaña: si el componente se
   vuelve a montar (o el efecto se re-ejecuta), reutiliza la misma promesa
   en vez de inyectar el script de nuevo. */
let promesaScriptJitsi = null;
function cargarScriptJitsi() {
  if (window.JitsiMeetExternalAPI) return Promise.resolve();
  if (!promesaScriptJitsi) {
    promesaScriptJitsi = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://${DOMINIO_JITSI}/external_api.js`;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error("No se pudo cargar el SDK de Jitsi"));
      document.head.appendChild(script);
    });
  }
  return promesaScriptJitsi;
}

/* El nombre de sala viaja como el último tramo de la ruta de salaUrl (ver
   generarSalaJitsi en apps-script.gs) — se reutiliza tal cual sin importar
   qué dominio traiga guardado esa URL. */
function extraerNombreSala(salaUrl) {
  try {
    return new URL(salaUrl).pathname.replace(/^\/+/, "");
  } catch {
    return "";
  }
}

/* Lee el payload del JWT sin validar la firma (eso lo hace el propio Jitsi
   al conectarse) — acá solo hace falta el nombre y el rol para mandarlos
   en el aviso de entrada. Forma del payload en generarJitsiJWT (apps-script.gs). */
function decodificarJWT(jwt) {
  try {
    const payload = jwt.split(".")[1];
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function PaginaSalaJitsi() {
  const token = new URLSearchParams(window.location.search).get("t") || "";
  const jwt = new URLSearchParams(window.location.search).get("token") || "";

  /* Rol de quien mira esta pestaña, según SU PROPIO JWT (?token=) — nunca
     confundir con el de la otra persona: cada quien recibe un JWT propio,
     firmado server-side con su rol correcto (generarJitsiJWT en
     apps-script.gs), así que decodificar ACÁ el JWT que trae la URL de
     ESTA pestaña siempre da el rol de quien la está mirando. Se usa para
     no mandar al tutor al formulario de calificación del estudiante (ver
     finalizarSesion() y la tarjeta de "sala cerrada" más abajo). */
  const datosJWT = jwt ? decodificarJWT(jwt) : null;
  const usuarioJWT = datosJWT?.context?.user || {};
  const esTutor = !!(usuarioJWT.moderator || usuarioJWT.affiliation === "owner");

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [sesion, setSesion] = useState(null);
  const [faltaParaAbrir, setFaltaParaAbrir] = useState(null);   // ms restantes, o 0 si ya abrió
  const [avisoTolerancia, setAvisoTolerancia] = useState(false);
  const [cerrada, setCerrada] = useState(false);
  const [salioAntes, setSalioAntes] = useState(false);

  const completadaRef = useRef(false);
  const contenedorRef = useRef(null);
  const jitsiApiRef = useRef(null);
  const entradaAvisadaRef = useRef(false);

  // 1) Verifica el token y trae el horario + el enlace crudo de Jitsi.
  useEffect(() => {
    if (!token) { setCargando(false); return; }
    let vivo = true;
    (async () => {
      try {
        const r = await api.obtenerSala(token);
        if (!vivo) return;
        if (r.ok) setSesion(r);
        else setError(MENSAJE_ERROR_SALA[r.error] || MENSAJE_ERROR_SALA.NO_ENCONTRADA);
      } catch {
        if (vivo) setError("No pudimos verificar su enlace. Revise su conexión y pruebe de nuevo.");
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [token]);

  /* Completa la sesión para AMBOS roles (es reentrante en el servidor, ver
     completarSesion() en apps-script.gs) pero solo el ESTUDIANTE va a
     /calificar — es el formulario para calificar al tutor, no tiene
     sentido que el tutor lo vea. El tutor simplemente se queda en esta
     página, que pasa a mostrar la tarjeta de "sala cerrada" (ver el
     render de acá abajo). */
  const finalizarSesion = () => {
    if (completadaRef.current) return;
    completadaRef.current = true;
    api.completarSesion(token).catch(() => {});
    if (!esTutor) irA_ruta(`/calificar?t=${token}`);
  };

  // 2) Reloj de la ventana de acceso: antes de horario / en curso (con aviso
  //    de tolerancia) / cierre automático al agotarse la tolerancia.
  //    useLayoutEffect (no useEffect) para que el primer cálculo pinte antes
  //    de que el navegador muestre el frame — si no, faltaParaAbrir sigue en
  //    null durante ese primer render y parpadea "0:00" al abrir la página.
  useLayoutEffect(() => {
    if (!sesion) return;
    const inicio = sesion.inicio ? new Date(sesion.inicio) : null;
    const fin = sesion.fin ? new Date(sesion.fin) : null;
    const abre = inicio ? new Date(inicio.getTime() - sesion.minutosAntes * 60000) : null;
    const finGracia = fin ? new Date(fin.getTime() + sesion.minutosDespues * 60000) : null;

    const tick = () => {
      const ahora = new Date();

      if (finGracia && ahora >= finGracia) {
        setFaltaParaAbrir(0);
        setCerrada(true);
        finalizarSesion();
        return;
      }

      if (abre && ahora < abre) {
        setFaltaParaAbrir(abre - ahora);
        return;
      }
      setFaltaParaAbrir(0);
      setAvisoTolerancia(!!(fin && ahora >= fin));
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesion]);

  const mostrandoSala = sesion && faltaParaAbrir === 0 && !cerrada && !error && !salioAntes;

  // 3) Arranca el SDK de Jitsi (external_api.js) apenas se muestra la sala,
  //    contra el self-hosted meet.ayudatetutorias.com con el JWT de la URL,
  //    y lo destruye al salir — nunca dos instancias vivas a la vez.
  //    Al primer "videoConferenceJoined" avisa al webhook de Apps Script
  //    para que quede registrada la entrada real a la videollamada.
  useEffect(() => {
    if (!mostrandoSala) return;
    let vivo = true;

    cargarScriptJitsi()
      .then(() => {
        if (!vivo || !contenedorRef.current) return;

        const roomName = extraerNombreSala(sesion.salaUrl);
        const jitsiApi = new window.JitsiMeetExternalAPI(DOMINIO_JITSI, {
          roomName,
          parentNode: contenedorRef.current,
          jwt: jwt || undefined,
          width: "100%",
          height: "100%",
          interfaceConfigOverwrite: {
            APP_NAME: "Ayudate",
            NATIVE_APP_NAME: "Ayudate",
            PROVIDER_NAME: "Ayudate",
            DEFAULT_BACKGROUND: "#09090B",
            DEFAULT_REMOTE_DISPLAY_NAME: "Participante",
            SHOW_JITSI_WATERMARK: false,
            SHOW_BRAND_WATERMARK: false,
            SHOW_POWERED_BY: false,
            MOBILE_APP_PROMO: false,
          },
          configOverwrite: {
            disableDeepLinking: true,
          },
        });
        jitsiApiRef.current = jitsiApi;

        jitsiApi.addEventListener("videoConferenceJoined", (evento) => {
          if (entradaAvisadaRef.current) return;
          entradaAvisadaRef.current = true;

          api.registrarEntradaSala(token, {
            roomName: evento?.roomName || roomName,
            nombre: usuarioJWT.name || evento?.displayName || "",
            rol: esTutor ? "tutor" : "estudiante",
            entradaEn: new Date().toISOString(),
          }).catch(() => {});
        });

        /* Se dispara cuando cualquiera de los dos cuelga desde dentro de la
           llamada (botón de colgar, o el tutor termina la reunión para
           todos) — nunca cuando es ESTA página la que llama a dispose() al
           cerrarse la ventana horaria, porque ahí se destruye el iframe de
           un tiro y no le da tiempo a Jitsi de avisar que alguien "se fue".
           Por eso no hace falta chequear "cerrada" acá: si ya cerró por
           horario, este listener ya no puede dispararse. */
        jitsiApi.addEventListener("videoConferenceLeft", () => {
          jitsiApi.dispose();
          jitsiApiRef.current = null;
          setSalioAntes(true);
        });
      })
      .catch(() => {});

    return () => {
      vivo = false;
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
        jitsiApiRef.current = null;
      }
      entradaAvisadaRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrandoSala]);

  return (
    <div className={mostrandoSala ? "v2 sala-full" : "v2"}>
      <style>{ESTILOS_MINIMOS}</style>
      <style>{`
        .sala-full{position:fixed;inset:0;min-height:0;display:flex;flex-direction:column;}
        .sala-frame{position:relative;flex:1;min-height:0;}
        .sala-frame iframe{width:100%;height:100%;border:0;display:block;}
        .sala-banner{position:absolute;top:16px;left:50%;transform:translateX(-50%);z-index:5;
          background:rgba(248,113,113,.95);color:#2A0A0A;font-weight:600;font-size:14px;
          padding:10px 20px;border-radius:999px;box-shadow:0 8px 24px rgba(0,0,0,.35);
          text-align:center;max-width:92vw;}
      `}</style>

      {mostrandoSala ? (
        <>
          {avisoTolerancia && (
            <div className="sala-banner" role="alert">
              ⚠️ Quedan {sesion.minutosDespues} minutos de tolerancia para finalizar la tutoría
            </div>
          )}
          <div className="sala-frame" ref={contenedorRef} />
        </>
      ) : (
        <div className="mini-wrap">
          <div className="logo" style={{ marginBottom: 28 }}><Marca /></div>

          {!token ? (
            <div className="mini-card">
              <h1>Enlace incompleto</h1>
              <p>
                Este enlace no trae el código de la sesión. Revise el correo que le enviamos y abra
                el enlace completo, o escríbanos por WhatsApp.
              </p>
              <a className="btn btn-p" href="/" onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
                Ir al inicio
              </a>
            </div>
          ) : cargando ? (
            <div className="mini-card"><p style={{ margin: 0 }}>Verificando su enlace…</p></div>
          ) : error ? (
            <div className="mini-card">
              <h1>No pudimos abrir la sala</h1>
              <p>{error}</p>
              <a className="btn btn-p" href="/" onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
                Ir al inicio
              </a>
            </div>
          ) : cerrada ? (
            <div className="mini-card">
              <h1>La sala ya cerró</h1>
              {esTutor ? (
                <p>Esta sesión terminó y la sala se cerró automáticamente.</p>
              ) : (
                <>
                  <p>
                    Esta sesión terminó y la sala se cerró automáticamente. Si todavía no calificó su
                    tutoría, revise el correo que le mandamos con el enlace.
                  </p>
                  <a className="btn btn-p" href={`/calificar?t=${token}`}
                    onClick={(e) => { e.preventDefault(); irA_ruta(`/calificar?t=${token}`); }}>
                    Calificar tutoría
                  </a>
                </>
              )}
            </div>
          ) : salioAntes ? (
            <div className="mini-card">
              <h1>Saliste de la sesión</h1>
              <p>Todavía queda tiempo en esta tutoría. Podés volver a entrar cuando quieras.</p>
              <a className="btn btn-p" href="#"
                onClick={(e) => { e.preventDefault(); setSalioAntes(false); }}>
                Volver a la sesión
              </a>
            </div>
          ) : sesion ? (
            <div className="proxima-sesion">
              <p className="proxima-sesion-eyebrow">Próxima sesión</p>
              <h1 className="proxima-sesion-curso">{sesion.curso}</h1>
              <p className="proxima-sesion-datos">
                Con {sesion.tutorNombre || sesion.estudianteNombre}
              </p>
              <p className="proxima-sesion-accion">
                Se habilita en <b className="proxima-sesion-cuenta">{formatearRestante(faltaParaAbrir)}</b>.
                Deje esta pestaña abierta — entra sola apenas se cumpla la hora, no hace falta que recargue.
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
